import { useState } from "react";
import type { CMSCTASection } from "@/mocks/cmsData";

interface Props {
  data: CMSCTASection;
  onChange: (data: CMSCTASection) => void;
}

export default function CMSCTAEditor({ data, onChange }: Props) {
  const [lang, setLang] = useState<"ar" | "en">("ar");

  const update = (field: keyof CMSCTASection, value: string) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="space-y-6">
      {/* Language Switcher Bar */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
        <div>
          <h3 className="text-sm font-bold text-gray-800">لغة المحتوى (Content Language)</h3>
          <p className="text-xs text-gray-500">اختر اللغة لتعديل نصوص قسم الدعوة للعمل (CTA)</p>
        </div>
        <div className="flex bg-gray-100 p-1 rounded-lg">
          <button
            type="button"
            onClick={() => setLang("ar")}
            className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
              lang === "ar" ? "bg-white text-[#2E4E45] shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            العربية (AR)
          </button>
          <button
            type="button"
            onClick={() => setLang("en")}
            className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
              lang === "en" ? "bg-white text-[#2E4E45] shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            English (EN)
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="text-sm font-bold text-[#2E4E45] mb-5 flex items-center gap-2">
          <i className="ri-megaphone-line text-base" />
          {lang === "ar" ? "نصوص قسم الدعوة للعمل (العربية)" : "Call to Action Texts (English)"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "الشارة (Badge)" : "Badge (EN)"}
            </label>
            <input
              type="text"
              value={lang === "ar" ? (data.badge || "") : (data.badge_en || "")}
              onChange={(e) => update(lang === "ar" ? "badge" : "badge_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "العنوان الرئيسي" : "Main Heading (EN)"}
            </label>
            <input
              type="text"
              value={lang === "ar" ? (data.heading || "") : (data.heading_en || "")}
              onChange={(e) => update(lang === "ar" ? "heading" : "heading_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "الوصف الرئيسي" : "Main Description (EN)"}
            </label>
            <textarea
              value={lang === "ar" ? (data.description || "") : (data.description_en || "")}
              onChange={(e) => update(lang === "ar" ? "description" : "description_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors resize-none"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "الوصف الثانوي" : "Sub Description (EN)"}
            </label>
            <textarea
              value={lang === "ar" ? (data.subDescription || "") : (data.subDescription_en || "")}
              onChange={(e) => update(lang === "ar" ? "subDescription" : "subDescription_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "نص زر الحجز" : "Booking Button (EN)"}
            </label>
            <input
              type="text"
              value={lang === "ar" ? (data.btnBook || "") : (data.btnBook_en || "")}
              onChange={(e) => update(lang === "ar" ? "btnBook" : "btnBook_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "نص زر التواصل" : "Contact Button (EN)"}
            </label>
            <input
              type="text"
              value={lang === "ar" ? (data.btnContact || "") : (data.btnContact_en || "")}
              onChange={(e) => update(lang === "ar" ? "btnContact" : "btnContact_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-[#2E4E45] rounded-xl p-6 text-center">
        <p className="text-xs text-white/50 mb-3">
          {lang === "ar" ? "معاينة مبسطة (العربية)" : "Simplified Preview (English)"}
        </p>
        <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 text-xs px-3 py-1 rounded-full mb-3 border border-white/15">
          {lang === "ar" ? (data.badge || "الشارة") : (data.badge_en || data.badge || "Badge")}
        </div>
        <h4 className="text-lg font-black text-white mb-2">
          {lang === "ar" ? (data.heading || "العنوان") : (data.heading_en || data.heading || "Heading")}
        </h4>
        <p className="text-white/60 text-sm mb-4">
          {lang === "ar" ? (data.description || "الوصف") : (data.description_en || data.description || "Description")}
        </p>
        <div className="flex gap-3 justify-center">
          <div className="px-5 py-2 bg-[#C8A96E] text-[#2E4E45] text-sm font-bold rounded-full whitespace-nowrap">
            {lang === "ar" ? (data.btnBook || "احجز") : (data.btnBook_en || data.btnBook || "Book")}
          </div>
          <div className="px-5 py-2 border border-white/30 text-white text-sm rounded-full whitespace-nowrap">
            {lang === "ar" ? (data.btnContact || "تواصل") : (data.btnContact_en || data.btnContact || "Contact")}
          </div>
        </div>
      </div>
    </div>
  );
}
